import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SKIP_AUTH } from '../config/auth';
import { 
  ScaleIcon, 
  CheckCircleIcon,
  EnvelopeIcon,
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  ArrowRightIcon
} from '../icons';

const Signup = () => {
  const { register, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    courtName: '',
    position: '',
    barNumber: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    agreeToPrivacy: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (SKIP_AUTH) {
      navigate('/login', { replace: true });
      return;
    }
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const positions = [
    'Judge',
    'Magistrate', 
    'Court Clerk',
    'Legal Researcher',
    'Court Administrator',
    'Judicial Assistant',
    'Other'
  ];

  const courtTypes = [
    'Supreme Court of Ghana',
    'Court of Appeal',
    'High Court',
    'Circuit Court',
    'District Court',
    'Special Court',
    'Tribunal',
    'Other Judicial Institution'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    if (!formData.courtName.trim()) {
      newErrors.courtName = 'Court/Institution name is required';
    }

    if (!formData.position.trim()) {
      newErrors.position = 'Position is required';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms of service';
    }

    if (!formData.agreeToPrivacy) {
      newErrors.agreeToPrivacy = 'You must agree to the privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});
    
    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        courtName: formData.courtName,
        position: formData.position,
        barNumber: formData.barNumber,
        password: formData.password
      };

      const result = await register(userData);
      
      if (result.success) {
        setRegistrationSuccess(true);
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      // console.error('Signup error:', error);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen gradient-legal flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <Link to="/" className="inline-flex justify-center">
            <img 
              src="/logo.png" 
              alt="Snwolley CourtScribe Logo" 
              className="h-16 w-16 rounded-2xl shadow-lg"
            />
          </Link>
          <h1 className="mt-6 text-3xl font-bold text-legal-900">
            Join Snwolley CourtScribe
          </h1>
          <p className="mt-2 text-sm text-legal-600">
            Create your account to access AI-powered court transcription
          </p>
          <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-court-100 text-court-800">
            <ShieldCheckIcon className="h-4 w-4 mr-2" />
            Exclusively for Ghanaian Legal Professionals
          </div>
        </div>

        {/* Form */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-legal-200">
          {registrationSuccess ? (
            /* Success Message */
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="flex items-center justify-center h-16 w-16 bg-green-100 rounded-full">
                  <CheckCircleIcon className="h-8 w-8 text-green-600 animate-pulse" />
                </div>
              </div>
              
              <div className="space-y-3">
                <h3 className="text-xl font-semibold text-legal-900">Registration Successful!</h3>
                <p className="text-sm text-legal-600">
                  Welcome <strong>{formData.firstName} {formData.lastName}</strong>! 
                  Your account has been created and is pending verification.
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm text-green-700">
                  <p className="font-medium">Next Steps:</p>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>Check your email for verification instructions</li>
                    <li>Complete your profile setup</li>
                    <li>Start transcribing court proceedings</li>
                  </ul>
                </div>
              </div>

              <Link
                to="/login"
                className="inline-flex items-center px-6 py-3 gradient-court text-white rounded-lg hover:from-court-600 hover:to-amber-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Go to Sign In
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-legal-900">Create Your Judicial Account</h2>
                <p className="text-sm text-legal-600 mt-2">Please provide your professional information</p>
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {errors.submit}
                </div>
              )}

              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-legal-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-legal-400" />
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500 ${
                        errors.firstName ? 'border-red-500' : 'border-legal-300'
                      }`}
                      placeholder="First name"
                    />
                  </div>
                  {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-legal-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-legal-400" />
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500 ${
                        errors.lastName ? 'border-red-500' : 'border-legal-300'
                      }`}
                      placeholder="Last name"
                    />
                  </div>
                  {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-legal-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-legal-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500 ${
                      errors.email ? 'border-red-500' : 'border-legal-300'
                    }`}
                    placeholder="your@court.gov.gh"
                  />
                </div>
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-legal-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-legal-400" />
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500 ${
                      errors.phoneNumber ? 'border-red-500' : 'border-legal-300'
                    }`}
                    placeholder="+233 20 000 0000"
                  />
                </div>
                {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
              </div>

              {/* Professional Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-legal-900 border-t border-legal-200 pt-4">
                  Professional Information
                </h3>

                {/* Court Name */}
                <div>
                  <label className="block text-sm font-medium text-legal-700 mb-2">
                    Court/Institution Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-legal-400" />
                    <select
                      value={formData.courtName}
                      onChange={(e) => handleInputChange('courtName', e.target.value)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500 appearance-none ${
                        errors.courtName ? 'border-red-500' : 'border-legal-300'
                      }`}
                    >
                      <option value="">Select your court/institution</option>
                      {courtTypes.map((court) => (
                        <option key={court} value={court}>{court}</option>
                      ))}
                    </select>
                  </div>
                  {errors.courtName && <p className="text-red-500 text-sm mt-1">{errors.courtName}</p>}
                </div>

                {/* Position */}
                <div>
                  <label className="block text-sm font-medium text-legal-700 mb-2">
                    Position <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <AcademicCapIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-legal-400" />
                    <select
                      value={formData.position}
                      onChange={(e) => handleInputChange('position', e.target.value)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500 appearance-none ${
                        errors.position ? 'border-red-500' : 'border-legal-300'
                      }`}
                    >
                      <option value="">Select your position</option>
                      {positions.map((position) => (
                        <option key={position} value={position}>{position}</option>
                      ))}
                    </select>
                  </div>
                  {errors.position && <p className="text-red-500 text-sm mt-1">{errors.position}</p>}
                </div>

                {/* Bar Number (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-legal-700 mb-2">
                    Bar Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.barNumber}
                    onChange={(e) => handleInputChange('barNumber', e.target.value)}
                    className="w-full px-3 py-3 border border-legal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500"
                    placeholder="If applicable"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-legal-900 border-t border-legal-200 pt-4">
                  Account Security
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-legal-700 mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className={`w-full pr-10 pl-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500 ${
                          errors.password ? 'border-red-500' : 'border-legal-300'
                        }`}
                        placeholder="Create password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-legal-400 hover:text-legal-600"
                      >
                        {showPassword ? (
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-legal-700 mb-2">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className={`w-full pr-10 pl-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-court-500 focus:border-court-500 ${
                          errors.confirmPassword ? 'border-red-500' : 'border-legal-300'
                        }`}
                        placeholder="Confirm password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-legal-400 hover:text-legal-600"
                      >
                        {showConfirmPassword ? (
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">🏛️ Judicial Verification Required</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>All accounts are verified for Ghana Judicial Service affiliation</li>
                      <li>Professional credentials will be validated</li>
                      <li>Typical verification time: 1-2 business days</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Terms and Privacy */}
              <div className="space-y-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.agreeToTerms}
                    onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                    className="mt-1 h-4 w-4 text-court-600 focus:ring-court-500 border-legal-300 rounded"
                  />
                  <span className="ml-3 text-sm text-legal-700">
                    I agree to the{' '}
                    <button type="button" className="text-court-600 hover:text-court-500 underline">
                      Terms of Service
                    </button>{' '}
                    and acknowledge that this platform is exclusively for Ghanaian legal professionals
                  </span>
                </label>
                {errors.agreeToTerms && <p className="text-red-500 text-sm">{errors.agreeToTerms}</p>}

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.agreeToPrivacy}
                    onChange={(e) => handleInputChange('agreeToPrivacy', e.target.checked)}
                    className="mt-1 h-4 w-4 text-court-600 focus:ring-court-500 border-legal-300 rounded"
                  />
                  <span className="ml-3 text-sm text-legal-700">
                    I agree to the{' '}
                    <button type="button" className="text-court-600 hover:text-court-500 underline">
                      Privacy Policy
                    </button>{' '}
                    and consent to the processing of my judicial data
                  </span>
                </label>
                {errors.agreeToPrivacy && <p className="text-red-500 text-sm">{errors.agreeToPrivacy}</p>}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-8 py-3 text-sm font-medium text-white gradient-court rounded-lg hover:from-court-600 hover:to-amber-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                  </div>
                ) : (
                  'Create Judicial Account'
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-legal-600">
            Already have an account?{' '}
            <Link to="/login" className="text-court-600 hover:text-court-700 font-medium">
              Sign in
            </Link>
          </p>
          <p className="text-xs text-legal-400 mt-2">
            © 2025 Snwolley CourtScribe. All rights reserved.
          </p>
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-10 -left-10 w-40 h-40 bg-court-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-1/3 -right-20 w-60 h-60 bg-amber-200 rounded-full opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute -bottom-20 left-1/4 w-80 h-80 bg-accent-200 rounded-full opacity-20 animate-pulse delay-2000"></div>
      </div>
    </div>
  );
};

export default Signup;